import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { FileDropZone } from "../FileDropZone";
import DateInput from "../DateInput";
import SupplierPicker from "../SupplierPicker";
import MarkupFields from "./MarkupFields";

export const EMPTY_QUICK_QUOTE = { vendor_id: "", vendor_name: "", vendor_email: "", vendor_phone: "", vendor_company: "", amount: "", description: "", validity_date: "", attachments: [], markup_a_pct: 0, markup_b_pct: 0, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up" };

/* Quick supplier quote — add (with supplier picker) or edit. Same form as the Job Request "Add Quote" dialog. */
export default function QuickQuoteDialog({ open, onOpenChange, initial, onSubmit, suppliers = [], markupTemplates = [], mode = "add", folder = "job-quotes", testPrefix = "quote" }) {
  const [form, setForm] = useState(initial || EMPTY_QUICK_QUOTE);
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";
  const t = isEdit ? `edit-${testPrefix}` : testPrefix;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_name.trim() || !form.amount) return;
    setSaving(true);
    try {
      await onSubmit({ ...form, amount: parseFloat(form.amount) || 0, markup_a_pct: parseFloat(form.markup_a_pct) || 0, markup_b_pct: parseFloat(form.markup_b_pct) || 0 });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-quote-dialog" : "add-quote-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">{isEdit ? "Edit Quote" : "Add Quote"}</DialogTitle>
          <DialogDescription>{isEdit ? "Update supplier quote details." : "Enter supplier quote details. All entries are timestamped."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {!isEdit && (
            <SupplierPicker suppliers={suppliers} selectedId={form.vendor_id}
              onPick={(s) => setForm(p => s ? { ...p, vendor_id: s.id || "", vendor_name: s.contact_name || s.name || p.vendor_name, vendor_email: s.email || p.vendor_email, vendor_phone: s.phone || p.vendor_phone, vendor_company: s.company_name || s.name || p.vendor_company } : { ...p, vendor_id: "" })} />
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Supplier / Contractor Name *</Label><Input value={form.vendor_name || ""} onChange={e => setForm({ ...form, vendor_name: e.target.value })} required data-testid={`${t}-vendor-name`} /></div>
            <div><Label>Email</Label><Input type="email" value={form.vendor_email || ""} onChange={e => setForm({ ...form, vendor_email: e.target.value })} data-testid={`${t}-vendor-email`} /></div>
            <div><Label>Phone</Label><Input value={form.vendor_phone || ""} onChange={e => setForm({ ...form, vendor_phone: e.target.value })} /></div>
            <div><Label>Company</Label><Input value={form.vendor_company || ""} onChange={e => setForm({ ...form, vendor_company: e.target.value })} /></div>
            <div><Label>Quote Amount (£) *</Label><Input type="number" step="0.01" value={form.amount ?? ""} onChange={e => setForm({ ...form, amount: e.target.value })} required data-testid={`${t}-amount`} /></div>
          </div>
          <div><Label>Valid Until</Label><DateInput value={form.validity_date || ""} onChange={e => setForm({ ...form, validity_date: e.target.value })} /></div>
          <div><Label>Description / Scope</Label><Textarea value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What does this quote cover?" data-testid={`${t}-description`} /></div>
          <MarkupFields form={form} setForm={setForm} templates={markupTemplates} />
          <div>
            <Label>Attachments</Label>
            <FileDropZone files={form.attachments || []} onFilesChange={(files) => setForm(p => ({ ...p, attachments: files }))} folder={folder} accept="all" maxFiles={5} compact className="mt-1" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={isEdit ? "save-quote-btn" : `${testPrefix}-submit-btn`}>{saving ? "Saving..." : isEdit ? "Save Quote" : "Add Quote"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
